import { Search } from "@/components/search/Search.tsx";
import { useTeamSearchInfinite } from "@/queries/use-teams.query.ts";
import { ApiTeamFilter, getTeamEmoji, TeamFilterDefault } from "@/types/team.types.ts";
import { Box, ListItem, Skeleton, Stack, Text } from "@skailer-com/uikit";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useNavigate, useParams } from "react-router-dom";
import { useDebounce } from "react-use";
import { twMerge } from "tailwind-merge";

export function TeamsListPTeamPageSidePanel() {
  const { t } = useTranslation();
  const { id } = useParams<{ id?: string }>();
  const { section } = useParams<{ section?: string }>();
  const navigate = useNavigate();

  const [filter, setFilter] = useState<ApiTeamFilter>(TeamFilterDefault);
  const [searchTerm, setSearchTerm] = useState(filter.text);
  const stripHtmlTags = (html?: string) => html?.replace(/<[^>]*>/g, "");

  const {
    data: teams,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useTeamSearchInfinite(filter);
  const isDataLoading = isLoading || isFetching || isFetchingNextPage;

  const activeTeamId = id ? parseInt(id, 10) : undefined;

  useEffect(() => {
    if (activeTeamId) return;
    if (
      !teams ||
      teams.pages.length == 0 ||
      !teams.pages[0] ||
      teams.pages[0].items.length == 0 ||
      !teams.pages[0].items[0]
    )
      return;

    const newActiveTeamId = teams.pages[0].items[0].id;
    navigate(`/employees/teams/${newActiveTeamId}${section ? `/${section}` : ""}`);
  }, [activeTeamId, teams, section, navigate]);

  const { inView, ref } = useInView({
    threshold: 0.5,
    skip: isDataLoading || !hasNextPage,
    delay: 500,
  });

  useEffect(() => {
    if (isDataLoading) return;
    if (
      teams &&
      teams.pages.length > 0 &&
      teams.pages[0] &&
      teams.pages[0].metadata.total_items == 0
    )
      return;
    if (inView) {
      fetchNextPage();
    }
  }, [inView, isDataLoading, teams, fetchNextPage]);

  useDebounce(
    () => {
      setFilter((prev) => ({
        ...prev,
        text: searchTerm,
        page_number: 1,
      }));
    },
    300,
    [searchTerm]
  );

  return (
    <Stack className="border-r w-76">
      <Stack
        className={"h-16 flex items-center px-6"}
        direction={"horizontal"}
        gap={"sm"}
        justify={"between"}
        items={"center"}
      >
        <Text className={"text-lg font-semibold truncate"}>{t("core:teams")}</Text>
      </Stack>
      <Search
        className={"w-full"}
        innerPadding={true}
        borderless={true}
        placeholder={t("common:search")}
        value={searchTerm}
        onInput={(e) => {
          setSearchTerm(e.currentTarget.value);
        }}
        clearable={true}
        onClear={() => {
          setSearchTerm("");
          setFilter(TeamFilterDefault);
        }}
      />

      <Stack>
        {teams?.pages.map((page, pageIndex) => (
          <Fragment key={`teams-page-${pageIndex}`}>
            {page?.items.map((team, index) => {
              const isLastItem =
                pageIndex === teams.pages.length - 1 && index === page.items.length - 1;
              return (
                <Box
                  className={twMerge("shrink-0", "w-full")}
                  ref={isLastItem ? ref : undefined}
                  key={`teams-page-item-${team.id}-${pageIndex}-${index}`}
                >
                  <ListItem
                    to={`/employees/teams/${team.id}`}
                    preTitle={team.title}
                    emphasis={"top"}
                    truncate={true}
                    title={stripHtmlTags(team.description)}
                    caption={`${team.members.length} ${t("core:people")}`}
                    emoji={getTeamEmoji(team)}
                    className={twMerge(
                      "p-4",
                      activeTeamId === team.id ? "bg-light" : "hover:bg-light"
                    )}
                  />
                </Box>
              );
            })}
          </Fragment>
        ))}
      </Stack>
      {isLoading && searchTerm == undefined && <Skeleton className={"w-full h-16"} />}
    </Stack>
  );
}
